"use client";

import React, { useMemo, useRef } from "react";
import { useToast } from "@/components/ui/Toast";

/** The toast context value changes on every toast; this wrapper is referentially stable for effect deps. */
export function useStableToast() {
  const toast = useToast();
  const ref = useRef(toast);
  ref.current = toast;
  return useMemo(() => ({
    success: (title: string, message?: string) => ref.current.success(title, message),
    error: (title: string, message?: string) => ref.current.error(title, message),
    warning: (title: string, message?: string) => ref.current.warning(title, message),
    info: (title: string, message?: string) => ref.current.info(title, message),
  }), []);
}

export type NotificationSeverity = "INFO" | "SUCCESS" | "WARNING" | "CRITICAL";

export interface InboxItem {
  id: string;
  eventId: string;
  accountId: string | null;
  readAt: string | null;
  createdAt: string;
  type: string;
  category: string;
  severity: NotificationSeverity;
  title: string;
  body: string;
  url?: string;
}

export const SEVERITY_STYLES: Record<NotificationSeverity, { dot: string; badge: string }> = {
  INFO: { dot: "bg-sky-400", badge: "bg-sky-500/10 text-sky-400 border-sky-500/30" },
  SUCCESS: { dot: "bg-emerald-400", badge: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  WARNING: { dot: "bg-amber-400", badge: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  CRITICAL: { dot: "bg-rose-500", badge: "bg-rose-500/10 text-rose-400 border-rose-500/30" },
};

export function SeverityDot({ severity }: { severity: NotificationSeverity }) {
  return <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_STYLES[severity]?.dot ?? "bg-themed-textMuted"}`} aria-hidden />;
}

export function SeverityBadge({ severity, label }: { severity: NotificationSeverity; label: string }) {
  return <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${SEVERITY_STYLES[severity]?.badge ?? ""}`}>{label}</span>;
}

export function formatRelativeTime(iso: string, language: string): string {
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(language === "ro" ? "ro" : "en", { numeric: "auto" });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), "second");
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
  if (abs < 86400 * 30) return rtf.format(Math.round(diff / 86400), "day");
  return new Date(iso).toLocaleDateString(language === "ro" ? "ro-RO" : "en-US");
}
